import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';

import { useIsCtaEditor } from '../../hooks';
import HomeRow from '../../Parts/HomeRow';
import AnnouncementBannerEditor from './AnnouncementBannerEditor';
import AnnouncementPopupEditor from './AnnouncementPopupEditor';
import { loadCtaContentFromApi, saveCtaContentUpdates } from '../ctaStorage';
import { hasCtaSchedule } from '../scheduleUtils';
import type { CtaContent } from '../types';
import type { fakeRootType } from '../../FakeRootType';

type EditCtaElementsProps = {
	ROOT: fakeRootType;
};

const EditCtaElements = ({ ROOT }: EditCtaElementsProps) => {
	const isCtaEditor = useIsCtaEditor();
	const [loading, setLoading] = useState(true);
	const [bannerText, setBannerText] = useState('');
	const [bannerLinkUrl, setBannerLinkUrl] = useState('');
	const [bannerScheduleStart, setBannerScheduleStart] = useState<string | null>(null);
	const [bannerScheduleEnd, setBannerScheduleEnd] = useState<string | null>(null);
	const [bannerTwoText, setBannerTwoText] = useState('');
	const [bannerTwoLinkUrl, setBannerTwoLinkUrl] = useState('');
	const [bannerTwoScheduleStart, setBannerTwoScheduleStart] = useState<string | null>(null);
	const [bannerTwoScheduleEnd, setBannerTwoScheduleEnd] = useState<string | null>(null);
	const [popupDraft, setPopupDraft] = useState({
		popupTitle: '',
		popupBody: '',
		popupImageUrl: null as string | null,
		popupScheduleStart: null as string | null,
		popupScheduleEnd: null as string | null,
	});
	const [popupTwoDraft, setPopupTwoDraft] = useState({
		popupTwoTitle: '',
		popupTwoBody: '',
		popupTwoImageUrl: null as string | null,
		popupTwoScheduleStart: null as string | null,
		popupTwoScheduleEnd: null as string | null,
	});
	const [savingBanner, setSavingBanner] = useState(false);
	const [savingBannerTwo, setSavingBannerTwo] = useState(false);
	const [savingPopup, setSavingPopup] = useState(false);
	const [savingPopupTwo, setSavingPopupTwo] = useState(false);
	const [clearingBanner, setClearingBanner] = useState(false);
	const [clearingBannerTwo, setClearingBannerTwo] = useState(false);
	const [clearingPopup, setClearingPopup] = useState(false);
	const [clearingPopupTwo, setClearingPopupTwo] = useState(false);

	const applyContent = (content: CtaContent) => {
		setBannerText(content.bannerText);
		setBannerLinkUrl(content.bannerLinkUrl ?? '');
		setBannerScheduleStart(content.bannerScheduleStart);
		setBannerScheduleEnd(content.bannerScheduleEnd);
		setBannerTwoText(content.bannerTwoText);
		setBannerTwoLinkUrl(content.bannerTwoLinkUrl ?? '');
		setBannerTwoScheduleStart(content.bannerTwoScheduleStart);
		setBannerTwoScheduleEnd(content.bannerTwoScheduleEnd);
		setPopupDraft({
			popupTitle: content.popupTitle,
			popupBody: content.popupBody,
			popupImageUrl: content.popupImageUrl,
			popupScheduleStart: content.popupScheduleStart,
			popupScheduleEnd: content.popupScheduleEnd,
		});
		setPopupTwoDraft({
			popupTwoTitle: content.popupTwoTitle,
			popupTwoBody: content.popupTwoBody,
			popupTwoImageUrl: content.popupTwoImageUrl,
			popupTwoScheduleStart: content.popupTwoScheduleStart,
			popupTwoScheduleEnd: content.popupTwoScheduleEnd,
		});
	};

	useEffect(() => {
		if (!isCtaEditor) {
			ROOT.loaded();
			return;
		}

		ROOT.setTitle('Edit CTA Elements');
		loadCtaContentFromApi()
			.then(applyContent)
			.finally(() => {
				setLoading(false);
				ROOT.loaded();
			});
	}, [ROOT, isCtaEditor]);

	const resetPopupDraft = () => {
		loadCtaContentFromApi().then(applyContent);
	};

	const confirmRemove = (kind: 'banner' | 'popup') => window.confirm(
		kind === 'banner'
			? 'Remove this announcement banner? It will stop showing for all users.'
			: 'Remove this announcement popup? It will stop showing for all users.',
	);

	const saveBanner = async () => {
		setSavingBanner(true);
		try {
			const scheduleEnabled = hasCtaSchedule({
				start: bannerScheduleStart,
				end: bannerScheduleEnd,
			});
			await saveCtaContentUpdates({
				bannerText: bannerText.trim(),
				bannerLinkUrl: bannerLinkUrl.trim(),
				bannerScheduleStart: scheduleEnabled ? bannerScheduleStart : null,
				bannerScheduleEnd: scheduleEnabled ? bannerScheduleEnd : null,
			});
			ROOT.toast('Banner saved', { type: 'success' });
		} catch {
			ROOT.toast('Failed to save banner', { type: 'error' });
		} finally {
			setSavingBanner(false);
		}
	};

	const saveBannerTwo = async () => {
		setSavingBannerTwo(true);
		try {
			const scheduleEnabled = hasCtaSchedule({
				start: bannerTwoScheduleStart,
				end: bannerTwoScheduleEnd,
			});
			await saveCtaContentUpdates({
				bannerTwoText: bannerTwoText.trim(),
				bannerTwoLinkUrl: bannerTwoLinkUrl.trim(),
				bannerTwoScheduleStart: scheduleEnabled ? bannerTwoScheduleStart : null,
				bannerTwoScheduleEnd: scheduleEnabled ? bannerTwoScheduleEnd : null,
			});
			ROOT.toast('Second banner saved', { type: 'success' });
		} catch {
			ROOT.toast('Failed to save second banner', { type: 'error' });
		} finally {
			setSavingBannerTwo(false);
		}
	};

	const savePopup = async () => {
		setSavingPopup(true);
		try {
			const scheduleEnabled = hasCtaSchedule({
				start: popupDraft.popupScheduleStart,
				end: popupDraft.popupScheduleEnd,
			});
			const updates: Partial<CtaContent> = {
				popupTitle: popupDraft.popupTitle.trim(),
				popupBody: popupDraft.popupBody.trim(),
				popupImageUrl: popupDraft.popupImageUrl || '',
				popupScheduleStart: scheduleEnabled ? popupDraft.popupScheduleStart : null,
				popupScheduleEnd: scheduleEnabled ? popupDraft.popupScheduleEnd : null,
			};
			await saveCtaContentUpdates(updates);
			ROOT.toast('Popup saved', { type: 'success' });
		} catch {
			ROOT.toast('Failed to save popup', { type: 'error' });
		} finally {
			setSavingPopup(false);
		}
	};

	const savePopupTwo = async () => {
		setSavingPopupTwo(true);
		try {
			const scheduleEnabled = hasCtaSchedule({
				start: popupTwoDraft.popupTwoScheduleStart,
				end: popupTwoDraft.popupTwoScheduleEnd,
			});
			const updates: Partial<CtaContent> = {
				popupTwoTitle: popupTwoDraft.popupTwoTitle.trim(),
				popupTwoBody: popupTwoDraft.popupTwoBody.trim(),
				popupTwoImageUrl: popupTwoDraft.popupTwoImageUrl || '',
				popupTwoScheduleStart: scheduleEnabled ? popupTwoDraft.popupTwoScheduleStart : null,
				popupTwoScheduleEnd: scheduleEnabled ? popupTwoDraft.popupTwoScheduleEnd : null,
			};
			await saveCtaContentUpdates(updates);
			ROOT.toast('Second popup saved', { type: 'success' });
		} catch {
			ROOT.toast('Failed to save second popup', { type: 'error' });
		} finally {
			setSavingPopupTwo(false);
		}
	};

	const clearBanner = async () => {
		if (!confirmRemove('banner')) return;
		setClearingBanner(true);
		try {
			await saveCtaContentUpdates({
				bannerText: '',
				bannerLinkUrl: '',
				bannerScheduleStart: null,
				bannerScheduleEnd: null,
			});
			setBannerText('');
			setBannerLinkUrl('');
			setBannerScheduleStart(null);
			setBannerScheduleEnd(null);
			ROOT.toast('Banner removed', { type: 'success' });
		} catch {
			ROOT.toast('Failed to remove banner', { type: 'error' });
		} finally {
			setClearingBanner(false);
		}
	};

	const clearBannerTwo = async () => {
		if (!confirmRemove('banner')) return;
		setClearingBannerTwo(true);
		try {
			await saveCtaContentUpdates({
				bannerTwoText: '',
				bannerTwoLinkUrl: '',
				bannerTwoScheduleStart: null,
				bannerTwoScheduleEnd: null,
			});
			setBannerTwoText('');
			setBannerTwoLinkUrl('');
			setBannerTwoScheduleStart(null);
			setBannerTwoScheduleEnd(null);
			ROOT.toast('Second banner removed', { type: 'success' });
		} catch {
			ROOT.toast('Failed to remove second banner', { type: 'error' });
		} finally {
			setClearingBannerTwo(false);
		}
	};

	const clearPopup = async () => {
		if (!confirmRemove('popup')) return;
		setClearingPopup(true);
		try {
			await saveCtaContentUpdates({
				popupTitle: '',
				popupBody: '',
				popupImageUrl: '',
				popupScheduleStart: null,
				popupScheduleEnd: null,
			});
			setPopupDraft({
				popupTitle: '',
				popupBody: '',
				popupImageUrl: null,
				popupScheduleStart: null,
				popupScheduleEnd: null,
			});
			ROOT.toast('Popup removed', { type: 'success' });
		} catch {
			ROOT.toast('Failed to remove popup', { type: 'error' });
		} finally {
			setClearingPopup(false);
		}
	};

	const clearPopupTwo = async () => {
		if (!confirmRemove('popup')) return;
		setClearingPopupTwo(true);
		try {
			await saveCtaContentUpdates({
				popupTwoTitle: '',
				popupTwoBody: '',
				popupTwoImageUrl: '',
				popupTwoScheduleStart: null,
				popupTwoScheduleEnd: null,
			});
			setPopupTwoDraft({
				popupTwoTitle: '',
				popupTwoBody: '',
				popupTwoImageUrl: null,
				popupTwoScheduleStart: null,
				popupTwoScheduleEnd: null,
			});
			ROOT.toast('Second popup removed', { type: 'success' });
		} catch {
			ROOT.toast('Failed to remove second popup', { type: 'error' });
		} finally {
			setClearingPopupTwo(false);
		}
	};

	if (!isCtaEditor || loading) {
		return null;
	}

	return (
		<HomeRow>
			<Container className="local-bootstrap">
				<AnnouncementBannerEditor
					heading="Announcement Banner"
					description="Displayed below the header after each login until the user dismisses it."
					linkHelpText="Optional. Leave blank to open the announcement popup when clicked."
					idPrefix="cta-banner"
					text={bannerText}
					linkUrl={bannerLinkUrl}
					scheduleStart={bannerScheduleStart}
					scheduleEnd={bannerScheduleEnd}
					onTextChange={setBannerText}
					onLinkUrlChange={setBannerLinkUrl}
					onScheduleStartChange={setBannerScheduleStart}
					onScheduleEndChange={setBannerScheduleEnd}
					onSave={saveBanner}
					onClear={clearBanner}
					saving={savingBanner}
					clearing={clearingBanner}
				/>

				<AnnouncementBannerEditor
					heading="Second Announcement Banner"
					description="Displayed directly below the first banner."
					linkHelpText={'Optional. Leave blank to open the second popup when clicked '
						+ '(or for plain, unclickable text if that popup is empty).'}
					idPrefix="cta-banner-two"
					text={bannerTwoText}
					linkUrl={bannerTwoLinkUrl}
					scheduleStart={bannerTwoScheduleStart}
					scheduleEnd={bannerTwoScheduleEnd}
					onTextChange={setBannerTwoText}
					onLinkUrlChange={setBannerTwoLinkUrl}
					onScheduleStartChange={setBannerTwoScheduleStart}
					onScheduleEndChange={setBannerTwoScheduleEnd}
					onSave={saveBannerTwo}
					onClear={clearBannerTwo}
					saving={savingBannerTwo}
					clearing={clearingBannerTwo}
				/>

				<AnnouncementPopupEditor
					heading="Announcement Popup"
					description={'Shown once per login until dismissed, and when the first banner is '
						+ 'clicked. Users can opt out until the content is updated.'}
					idPrefix="cta-popup"
					allowHideUntilUpdate
					title={popupDraft.popupTitle}
					body={popupDraft.popupBody}
					imageUrl={popupDraft.popupImageUrl}
					scheduleStart={popupDraft.popupScheduleStart}
					scheduleEnd={popupDraft.popupScheduleEnd}
					onTitleChange={(popupTitle) => setPopupDraft((draft) => ({ ...draft, popupTitle }))}
					onBodyChange={(popupBody) => setPopupDraft((draft) => ({ ...draft, popupBody }))}
					onImageChange={(popupImageUrl) => setPopupDraft((draft) => ({ ...draft, popupImageUrl }))}
					onScheduleStartChange={(popupScheduleStart) => setPopupDraft((draft) => ({
						...draft,
						popupScheduleStart,
					}))}
					onScheduleEndChange={(popupScheduleEnd) => setPopupDraft((draft) => ({
						...draft,
						popupScheduleEnd,
					}))}
					onSave={savePopup}
					onClear={clearPopup}
					onCancel={resetPopupDraft}
					saving={savingPopup}
					clearing={clearingPopup}
				/>

				<AnnouncementPopupEditor
					heading="Second Announcement Popup"
					description={'Opens only when the second banner is clicked, so it can hold a longer '
						+ 'message than the banner itself. Never shown on its own at login.'}
					idPrefix="cta-popup-two"
					allowHideUntilUpdate={false}
					title={popupTwoDraft.popupTwoTitle}
					body={popupTwoDraft.popupTwoBody}
					imageUrl={popupTwoDraft.popupTwoImageUrl}
					scheduleStart={popupTwoDraft.popupTwoScheduleStart}
					scheduleEnd={popupTwoDraft.popupTwoScheduleEnd}
					onTitleChange={(popupTwoTitle) => setPopupTwoDraft((draft) => ({
						...draft,
						popupTwoTitle,
					}))}
					onBodyChange={(popupTwoBody) => setPopupTwoDraft((draft) => ({
						...draft,
						popupTwoBody,
					}))}
					onImageChange={(popupTwoImageUrl) => setPopupTwoDraft((draft) => ({
						...draft,
						popupTwoImageUrl,
					}))}
					onScheduleStartChange={(popupTwoScheduleStart) => setPopupTwoDraft((draft) => ({
						...draft,
						popupTwoScheduleStart,
					}))}
					onScheduleEndChange={(popupTwoScheduleEnd) => setPopupTwoDraft((draft) => ({
						...draft,
						popupTwoScheduleEnd,
					}))}
					onSave={savePopupTwo}
					onClear={clearPopupTwo}
					onCancel={resetPopupDraft}
					saving={savingPopupTwo}
					clearing={clearingPopupTwo}
				/>
			</Container>
		</HomeRow>
	);
};

export default EditCtaElements;
